import { useCallback, useMemo, useState } from 'react';
import dayjs from 'dayjs';

import type { CalendarDayAggregate } from '../types';

type Viewport = {
  start: Date | null;
  end: Date | null;
  viewType?: string | null;
};

type RangeArg = {
  start?: Date;
  end?: Date;
  viewType?: string;
};

export function useCalendarViewportCounts(
  calendarData: CalendarDayAggregate[]
) {
  const [viewport, setViewport] = useState<Viewport>({
    start: null,
    end: null,
    viewType: null,
  });

  const handleRangeChange = useCallback((arg: RangeArg) => {
    setViewport({
      start: arg?.start ?? null,
      end: arg?.end ?? null,
      viewType: arg?.viewType ?? null,
    });
  }, []);

  const visibleItems = useMemo(() => {
    const start = viewport.start;
    const end = viewport.end;

    if (!start || !end) return calendarData ?? [];

    const startMs = dayjs(start).valueOf();
    const endMs = dayjs(end).valueOf();

    return (calendarData ?? []).filter((agg) => {
      if (!agg.date) return false;
      const ms = dayjs(agg.date).valueOf();
      return ms >= startMs && ms < endMs;
    });
  }, [calendarData, viewport.start, viewport.end]);

  const counts = useMemo(() => {
    const items = visibleItems ?? [];

    const critical = items.reduce((sum, agg) => sum + (agg.critical ?? 0), 0);
    const atRisk = items.reduce((sum, agg) => sum + (agg.at_risk ?? 0), 0);
    const noIssue = items.reduce((sum, agg) => sum + (agg.no_issue ?? 0), 0);
    const totalVisible = items.reduce((sum, agg) => sum + (agg.total ?? 0), 0);
    const active = critical + atRisk;

    return {
      totalVisible,
      active,
      critical,
      atRisk,
      noIssue,
    };
  }, [visibleItems]);

  return {
    viewport,
    handleRangeChange,
    visibleItems,
    counts,
  };
}
